/**
 * My Reviews Page — Dedicated reviews tracking for assistants
 * Shows all received reviews, pending requests, and stats
 */
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NavBar, NavActions } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import {
  ArrowLeft, Star, Mail, Send, Clock, CheckCircle2, User, Loader2,
} from "lucide-react";
import { WORK_TYPES } from "@/lib/profileOptions";

function getLabel(value) {
  var wt = WORK_TYPES.find(function(w) { return w.value === value; });
  return wt ? wt.label : value;
}

function Stars({ rating, size = "w-4 h-4" }) {
  return (
    <span className="inline-flex gap-0.5">
      {[0, 1, 2, 3, 4].map(function(i) {
        return (
          <Star
            key={i}
            className={size + " " + (i < Math.round(rating) ? "text-yellow-400 fill-yellow-400" : "text-white/20")}
          />
        );
      })}
    </span>
  );
}

export default function MyReviewsPage() {
  var navigate = useNavigate();
  var auth = useAuth();
  var user = auth.user;
  var token = auth.token;

  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [tab, setTab] = useState("reviews");

  useEffect(function() {
    if (token) fetchData();
  }, [token]);

  async function fetchData() {
    try {
      var res = await fetch(API + "/reviews/my-reviews", {
        headers: { Authorization: "Bearer " + token },
      });
      if (res.ok) {
        var d = await res.json();
        setData(d);
      } else {
        toast.error("Failed to load reviews");
      }
    } catch(e) {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/30" />
      </div>
    );
  }

  var reviews = data ? data.reviews : [];
  var requests = data ? data.requests : [];
  var stats = data ? data.stats : {};
  var pendingRequests = requests.filter(function(r) { return r.status === "pending"; });
  var completedRequests = requests.filter(function(r) { return r.status === "completed"; });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar>
        <Link to="/dashboard" className="text-sm font-medium text-white/60 hover:text-white transition-colors uppercase tracking-wider">
          Dashboard
        </Link>
        <NavActions />
      </NavBar>

      <main className="flex-1 pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-[1000px] mx-auto">
          <button
            onClick={function() { navigate("/dashboard"); }}
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-8"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-wider">Dashboard</span>
          </button>

          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-8" data-testid="my-reviews-title">
            My Reviews
          </h1>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10" data-testid="reviews-stats">
            <Card className="bg-white/[0.03] border-white/5 p-5 text-center">
              <p className="text-3xl font-bold">{stats.average_rating ? stats.average_rating.toFixed(1) : "—"}</p>
              <div className="flex justify-center mt-1 mb-1">
                {stats.average_rating ? <Stars rating={stats.average_rating} size="w-3.5 h-3.5" /> : null}
              </div>
              <p className="text-white/40 text-xs uppercase tracking-wider">Avg Rating</p>
            </Card>
            <Card className="bg-white/[0.03] border-white/5 p-5 text-center">
              <p className="text-3xl font-bold">{stats.total_reviews || 0}</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-2">Total Reviews</p>
            </Card>
            <Card className="bg-white/[0.03] border-white/5 p-5 text-center">
              <p className="text-3xl font-bold">{stats.would_hire_again_pct != null ? stats.would_hire_again_pct + "%" : "—"}</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-2">Would Hire Again</p>
            </Card>
            <Card className="bg-white/[0.03] border-white/5 p-5 text-center">
              <p className="text-3xl font-bold">{stats.platform_reviews || 0}</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-2">From Jobs</p>
            </Card>
            <Card className="bg-white/[0.03] border-white/5 p-5 text-center">
              <p className="text-3xl font-bold">{stats.external_reviews || 0}</p>
              <p className="text-white/40 text-xs uppercase tracking-wider mt-2">From Requests</p>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-8 border-b border-white/10">
            <button
              onClick={function() { setTab("reviews"); }}
              className={"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px " + (tab === "reviews" ? "text-white border-coral" : "text-white/50 border-transparent hover:text-white/70")}
              data-testid="tab-reviews"
            >
              Reviews ({reviews.length})
            </button>
            <button
              onClick={function() { setTab("requests"); }}
              className={"px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px " + (tab === "requests" ? "text-white border-coral" : "text-white/50 border-transparent hover:text-white/70")}
              data-testid="tab-requests"
            >
              Requests ({requests.length})
              {pendingRequests.length > 0 && (
                <span className="ml-1.5 bg-coral/20 text-coral text-xs px-1.5 py-0.5 rounded-full">{pendingRequests.length}</span>
              )}
            </button>
          </div>

          {/* Reviews Tab */}
          {tab === "reviews" && (
            <div className="space-y-4" data-testid="reviews-list">
              {reviews.length === 0 ? (
                <Card className="bg-white/[0.03] border-white/5 p-12 text-center">
                  <Star className="w-12 h-12 text-white/15 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No reviews yet</h3>
                  <p className="text-white/50 mb-6 text-sm">Request reviews from studios you've worked with to build your reputation.</p>
                </Card>
              ) : (
                reviews.map(function(r) {
                  var isExternal = r.source === "external_request";
                  return (
                    <Card key={r.review_id} className="bg-white/[0.03] border-white/5 p-6" data-testid={"review-item-" + r.review_id}>
                      <div className="flex items-start gap-4">
                        <div className="shrink-0">
                          {r.reviewer && r.reviewer.profile_image ? (
                            <Avatar className="w-10 h-10 border border-white/10">
                              <AvatarImage src={r.reviewer.profile_image} />
                              <AvatarFallback className="bg-white/[0.05] text-white text-sm">
                                {(r.reviewer_name || r.reviewer?.name || "?").charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-white/[0.06] flex items-center justify-center">
                              <User className="w-5 h-5 text-white/30" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3 mb-1">
                            <div>
                              <p className="font-semibold text-sm">{r.reviewer_name || (r.reviewer && r.reviewer.name) || "Anonymous"}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <Stars rating={r.rating} size="w-3.5 h-3.5" />
                                <span className="text-white/40 text-xs">{r.rating}/5</span>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <span className="text-white/30 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
                              {isExternal && (
                                <Badge className="block mt-1 bg-white/[0.06] text-white/50 border-white/10 text-[10px]">External</Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-white/60 text-sm mt-2 leading-relaxed">{r.comment}</p>
                          {r.would_hire_again != null && (
                            <div className="mt-2">
                              <Badge className={r.would_hire_again ? "bg-green-500/15 text-green-400 border-green-500/20 text-[10px]" : "bg-red-500/15 text-red-400 border-red-500/20 text-[10px]"}>
                                {r.would_hire_again ? "Would hire again" : "Would not hire again"}
                              </Badge>
                            </div>
                          )}
                          {r.roles_performed && r.roles_performed.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                              {r.roles_performed.map(function(role) {
                                return (
                                  <Badge key={role} className="bg-white/[0.05] text-white/50 border-white/10 text-[10px]">
                                    {getLabel(role)}
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {/* Requests Tab */}
          {tab === "requests" && (
            <div data-testid="requests-list">
              <div className="flex items-center justify-between mb-6">
                <p className="text-white/40 text-sm">
                  {data ? data.remaining_requests : 0} of {data ? data.monthly_limit : 5} requests remaining this month
                </p>
              </div>

              {requests.length === 0 ? (
                <Card className="bg-white/[0.03] border-white/5 p-12 text-center">
                  <Mail className="w-12 h-12 text-white/15 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No requests sent yet</h3>
                  <p className="text-white/50 text-sm">Use the "Request Reviews" button on your dashboard to get started.</p>
                </Card>
              ) : (
                <div className="space-y-8">
                  {/* Pending Requests Section */}
                  {pendingRequests.length > 0 && (
                    <div data-testid="pending-requests-section">
                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="w-4 h-4 text-amber-400" />
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-amber-400">
                          Awaiting Response ({pendingRequests.length})
                        </h3>
                      </div>
                      <div className="space-y-3">
                        {pendingRequests.map(function(req) {
                          return (
                            <Card key={req.request_id} className="bg-amber-500/[0.04] border-amber-500/10 p-5" data-testid={"pending-request-" + req.request_id}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0">
                                    <Send className="w-4 h-4 text-amber-400" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate" data-testid={"pending-request-name-" + req.request_id}>
                                      {req.recipient_name || req.recipient_email}
                                    </p>
                                    {req.recipient_name && (
                                      <p className="text-white/40 text-xs truncate" data-testid={"pending-request-email-" + req.request_id}>
                                        {req.recipient_email}
                                      </p>
                                    )}
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-white/30 text-xs" data-testid={"pending-request-date-" + req.request_id}>
                                        Sent {new Date(req.created_at).toLocaleDateString()}
                                      </span>
                                      {req.roles_performed && req.roles_performed.length > 0 && (
                                        <span className="text-white/30 text-xs">
                                          {req.roles_performed.map(function(r) { return getLabel(r); }).join(", ")}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 text-xs" data-testid={"pending-request-badge-" + req.request_id}>
                                  Pending
                                </Badge>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Completed Requests Section */}
                  {completedRequests.length > 0 && (
                    <div data-testid="completed-requests-section">
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-white/40">
                          Completed ({completedRequests.length})
                        </h3>
                      </div>
                      <div className="space-y-3">
                        {completedRequests.map(function(req) {
                          return (
                            <Card key={req.request_id} className="bg-white/[0.03] border-white/5 p-5" data-testid={"completed-request-" + req.request_id}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-9 h-9 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium truncate">
                                      {req.recipient_name || req.recipient_email}
                                    </p>
                                    {req.recipient_name && (
                                      <p className="text-white/40 text-xs truncate">{req.recipient_email}</p>
                                    )}
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-white/30 text-xs">{new Date(req.created_at).toLocaleDateString()}</span>
                                      {req.roles_performed && req.roles_performed.length > 0 && (
                                        <span className="text-white/30 text-xs">
                                          {req.roles_performed.map(function(r) { return getLabel(r); }).join(", ")}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <Badge className="bg-green-500/15 text-green-400 border-green-500/20 text-xs">
                                  Completed
                                </Badge>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
